import { z } from 'zod';
import { ApiEnvelopeSchema } from './schemas';

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const getAuthHeaders = (): Record<string, string> => {
  const idToken = sessionStorage.getItem('id_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  return headers;
};

const apiGetValidated = async <T>(url: string, schema: z.ZodType<T>): Promise<T> => {
  const resp = await fetch(url, { headers: getAuthHeaders() });
  const json: unknown = await resp.json();
  if (!resp.ok) {
    const envelope = json as { error?: string };
    throw new ApiError(envelope.error ?? `Request failed: ${resp.status}`, resp.status);
  }
  const parsed = ApiEnvelopeSchema(schema).parse(json);
  return parsed.data;
};

const apiPostValidated = async <T>(url: string, body: Record<string, unknown>, schema: z.ZodType<T>): Promise<T> => {
  const resp = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const json: unknown = await resp.json();
  if (!resp.ok) {
    const envelope = json as { error?: string };
    throw new ApiError(envelope.error ?? `Request failed: ${resp.status}`, resp.status);
  }
  const parsed = ApiEnvelopeSchema(schema).parse(json);
  return parsed.data;
};

/** @deprecated Use apiGetValidated with a Zod schema */
const apiGet = async <T>(url: string): Promise<T> => {
  const resp = await fetch(url, { headers: getAuthHeaders() });
  const json = (await resp.json()) as { success: boolean; data: T; error?: string };
  if (!resp.ok) {
    throw new ApiError(json.error ?? `Request failed: ${resp.status}`, resp.status);
  }
  return json.data;
};

/** @deprecated Use apiPostValidated with a Zod schema */
const apiPost = async <T>(url: string, body: Record<string, unknown>): Promise<T> => {
  const resp = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const json = (await resp.json()) as { success: boolean; data: T; error?: string };
  if (!resp.ok) {
    throw new ApiError(json.error ?? `Request failed: ${resp.status}`, resp.status);
  }
  return json.data;
};

export { ApiError, apiGet, apiPost, apiGetValidated, apiPostValidated, getAuthHeaders };
